// client/src/views/Interactive_parts/OfflineUploadToServer.js
import React, { useState, useEffect } from "react";
import { X, Database } from "lucide-react";
import { getOfflineMap } from "../../utils/offlineDb";

function normalizeColorToInt(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return value;

  let s = String(value).trim();
  if (!s) return 0;

  // Handle things like "#c8ff00" or "c8ff00"
  if (s.startsWith("#")) s = s.slice(1);

  // If it's already numeric-as-string, just parse base 10
  if (/^\d+$/.test(s)) {
    const n10 = parseInt(s, 10);
    return Number.isNaN(n10) ? 0 : n10;
  }

  // Otherwise treat as hex
  const n16 = parseInt(s, 16);
  return Number.isNaN(n16) ? 0 : n16;
}

function OfflineUploadToServer({ mapId, user }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [offlineMap, setOfflineMap] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [gameName, setGameName] = useState("");
  const [effectiveUser, setEffectiveUser] = useState(user || null);
  const [checkingUser, setCheckingUser] = useState(false);

  useEffect(() => {
    if (!open) return;

    setError("");
    setSuccess("");

    if (user && user.id) {
      setEffectiveUser(user);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setCheckingUser(true);
        const baseUrl = window.location.origin || "";
        const res = await fetch(`${baseUrl}/api/me`, {
          credentials: "include",
        });
        if (!res.ok) {
          if (!cancelled) setEffectiveUser(null);
          return;
        }
        const json = await res.json();
        if (!cancelled) {
          if (json && json.id) setEffectiveUser(json);
          else setEffectiveUser(null);
        }
      } catch {
        if (!cancelled) setEffectiveUser(null);
      } finally {
        if (!cancelled) setCheckingUser(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, user]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    (async () => {
      try {
        const om = await getOfflineMap(mapId);
        if (cancelled) return;
        setOfflineMap(om || {});
        const meta = om && om.meta ? om.meta : {};
        const metaName =
          meta.name ||
          meta.map_name ||
          om.display_name ||
          om.name ||
          "";
        const metaGame =
          meta.game_name ||
          meta.game ||
          om.game_name ||
          om.game ||
          "";
        setDisplayName(metaName);
        setGameName(metaGame);
      } catch {
        if (!cancelled) setError("Failed to load offline map data.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, mapId]);

  function normalizeArray(v) {
    if (!v) return [];
    if (Array.isArray(v)) return v;
    return [];
  }

  async function handleUpload(e) {
    e.preventDefault();

    if (!offlineMap) {
      setError("Offline map not loaded yet.");
      return;
    }
    if (!offlineMap.image) {
      setError("This offline map has no image stored and cannot be uploaded.");
      return;
    }
    if (!effectiveUser || !effectiveUser.id) {
      setError("You must be logged in to upload.");
      return;
    }
    if (!gameName.trim()) {
      setError("Please enter a game name.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const baseUrl = window.location.origin || "";
      const meta = offlineMap.meta || {};

      const formData = new FormData();
      formData.append("game_name", gameName.trim());
      formData.append(
        "display_name",
        displayName.trim() || gameName.trim()
      );
      formData.append("version_id", meta.version_id || "");
      formData.append("uploader_id", String(effectiveUser.id));
      formData.append("mode", "default");

      const tipUrl = meta.tip_url || offlineMap.tip_url || "";
      if (tipUrl) formData.append("tip_url", tipUrl);

      const filename = meta.image_name || "offline-map.png";
      formData.append("map", offlineMap.image, filename);

      const uploadRes = await fetch(`${baseUrl}/api/upload-map`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!uploadRes.ok) {
        const t = await uploadRes.text().catch(() => "");
        console.error("Upload map failed:", uploadRes.status, t);
        throw new Error("Upload failed.");
      }

      const uploadJson = await uploadRes.json().catch((err) => {
        console.error("Failed to parse upload response JSON:", err);
        throw err;
      });

      const newMapId = uploadJson.id;
      if (!newMapId) {
        console.error("Upload response missing map id:", uploadJson);
        throw new Error("No map ID returned from upload.");
      }

      const offlineCategories = normalizeArray(offlineMap.poi_categories);
      const offlineTypes = normalizeArray(offlineMap.poi_types);
      const offlinePois = normalizeArray(offlineMap.pois);
      const offlineZones = normalizeArray(
        offlineMap.zones || offlineMap.geoman_shapes
      );

      const baseHeaders = {
        "Content-Type": "application/json",
      };

      // 1) Categories
      const categoryIdMap = new Map();
      for (const cat of offlineCategories) {
        const name =
          (cat && cat.name) || (typeof cat === "string" ? cat : "");
        if (!name) continue;

        const res = await fetch(`${baseUrl}/api/poi/category`, {
          method: "POST",
          headers: baseHeaders,
          credentials: "include",
          body: JSON.stringify({
            name,
            map_id: newMapId,
            user_id: effectiveUser.id,
          }),
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          console.error("Category sync failed:", {
            cat,
            status: res.status,
            txt,
          });
          throw new Error("Failed to sync categories.");
        }

        const json = await res.json().catch((err) => {
          console.error("Category response JSON parse failed:", err);
          throw err;
        });

        if (json && json.id != null) {
          const key = cat && cat.id != null ? cat.id : name;
          categoryIdMap.set(key, json.id);
        }
      }

      // 2) Types (including style via /api/type-edit)
      const typeIdMap = new Map();
      const categoryForType = new Map();
      for (const t of offlineTypes) {
        const name =
          (t && t.name) || (typeof t === "string" ? t : "");
        if (!name) continue;

        const oldCatKey =
          t.category_id != null
            ? t.category_id
            : offlineCategories.find(
                (c) => c && c.name === t.category
              )?.id;

        const newCategoryId = categoryIdMap.get(oldCatKey) || null;
        if (!newCategoryId) continue;

        const rawColor = t.color || t.colour || "#ff0000";
        const colorInt = normalizeColorToInt(rawColor);
        const icon = t.shape || t.icon || "circle";

        const res = await fetch(`${baseUrl}/api/poi/type`, {
          method: "POST",
          headers: baseHeaders,
          credentials: "include",
          body: JSON.stringify({
            name,
            map_id: newMapId,
            category_id: newCategoryId,
            color: colorInt,
            icon,
          }),
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          console.error("Type sync failed:", { t, status: res.status, txt });
          throw new Error("Failed to sync types.");
        }

        const json = await res.json().catch((err) => {
          console.error("Type response JSON parse failed:", err);
          throw err;
        });

        if (json && json.id != null) {
          const newTypeId = json.id;
          const typeKey = t && t.id != null ? t.id : name;
          typeIdMap.set(typeKey, newTypeId);
          categoryForType.set(typeKey, newCategoryId);

          // Push label_border / label_bg_colour using existing typeEdit route
          const labelBorder =
            t.label_border !== undefined ? t.label_border : undefined;
          const labelBgColour =
            t.label_border_colour ||
            t.label_bg_colour ||
            undefined;

          if (labelBorder !== undefined || labelBgColour !== undefined) {
            const payload = {
              name,
              color: colorInt,
              icon,
              category_id: newCategoryId,
              map_id: newMapId,
              label_border:
                labelBorder !== undefined ? labelBorder : 1,
              label_bg_colour: labelBgColour || "#000000",
            };

            try {
              const editRes = await fetch(
                `${baseUrl}/api/type-edit/${newTypeId}`,
                {
                  method: "PUT",
                  headers: baseHeaders,
                  credentials: "include",
                  body: JSON.stringify(payload),
                }
              );
              if (!editRes.ok) {
                const txt = await editRes.text().catch(() => "");
                console.warn(
                  "Type style update failed (continuing anyway):",
                  { payload, status: editRes.status, txt }
                );
              }
            } catch (err) {
              console.warn(
                "Type style update threw (continuing anyway):",
                err
              );
            }
          }
        }
      }

      // 3) POIs
      for (const p of offlinePois) {
        const origTypeId =
          p.type_id != null ? p.type_id : p.type;
        const newTypeId = typeIdMap.get(origTypeId) || null;
        const newCategoryId = categoryForType.get(origTypeId) || null;
        if (!newCategoryId || !newTypeId) continue;

        const body = {
          map_id: newMapId,
          category_id: newCategoryId,
          type_id: newTypeId,
          x: p.x,
          y: p.y,
          description: p.description || "",
          created_by: effectiveUser.id,
          poi_name: p.poi_name || p.name || null,
          image_url: p.image_url || null,
        };

        const res = await fetch(`${baseUrl}/api/poi/save`, {
          method: "POST",
          headers: baseHeaders,
          credentials: "include",
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          console.error("POI sync failed:", {
            body,
            status: res.status,
            txt,
          });
          throw new Error("Failed to sync POIs.");
        }
      }

      // 4) Zones / Geoman shapes
      for (const z of offlineZones) {
        if (!z || !z.geometry) continue;

        const strokeOpacity =
          z.stroke_opacity != null
            ? z.stroke_opacity
            : z.strokeOpacity != null
            ? z.strokeOpacity
            : 1;
        const fillOpacity =
          z.fill_opacity != null
            ? z.fill_opacity
            : z.fillOpacity != null
            ? z.fillOpacity
            : 0.5;

        const body = {
          mapId: newMapId,
          userId: effectiveUser.id,
          label: z.label || "",
          geometry: z.geometry,
          fillColor: z.fillColor || "#ff0000",
          strokeColor: z.strokeColor || "#ff0000",
          strokeOpacity,
          fillOpacity,
          pattern: z.pattern || "",
          radius: z.radius || null,
          shapeType: z.shapeType || "",
        };

        const res = await fetch(`${baseUrl}/api/polygons`, {
          method: "POST",
          headers: baseHeaders,
          credentials: "include",
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          console.error("Zone sync failed:", {
            body,
            status: res.status,
            txt,
          });
          throw new Error("Failed to sync zones.");
        }
      }

      setSuccess("Upload complete. Redirecting to new map...");

      if (uploadJson.slug && uploadJson.uploader_username) {
        window.location.href = `/map/${uploadJson.slug}_${uploadJson.uploader_username}_${newMapId}`;
      } else if (uploadJson.slug) {
        window.location.href = `/map/${uploadJson.slug}_${newMapId}`;
      } else {
        window.location.href = `/map/${newMapId}`;
      }
    } catch (err) {
      console.error("Failed to upload offline map:", err);
      setError(err && err.message ? err.message : "Upload failed.");
      setSaving(false);
    }
  }

  const hasImage = !!(offlineMap && offlineMap.image);

  if (!mapId) return null;

  return (
    <>
      <button
        className="map-controls-btn"
        type="button"
        onClick={() => {
          if (saving) return;
          setError("");
          setSuccess("");
          setOpen(true);
        }}
        title="Upload this offline map to Point Atlas"
      >
        <Database />
      </button>

      {open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "#000000aa",
            zIndex: 2500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#111",
              color: "#fff",
              padding: 20,
              borderRadius: 12,
              width: "100%",
              maxWidth: 420,
              boxShadow: "0 0 20px #000",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                }}
              >
                Upload offline map
              </h2>
              <button
                type="button"
                onClick={() => {
                  if (!saving) setOpen(false);
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#aaa",
                  cursor: "pointer",
                }}
              >
                <X />
              </button>
            </div>

            <form onSubmit={handleUpload}>
              <div style={{ marginBottom: 10 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 13,
                    marginBottom: 4,
                  }}
                >
                  Game name
                </label>
                <input
                  type="text"
                  value={gameName}
                  onChange={(e) => setGameName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    borderRadius: 6,
                    border: "1px solid #444",
                    background: "#000",
                    color: "#fff",
                  }}
                />
              </div>

              <div style={{ marginBottom: 10 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 13,
                    marginBottom: 4,
                  }}
                >
                  Map display name
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    borderRadius: 6,
                    border: "1px solid #444",
                    background: "#000",
                    color: "#fff",
                  }}
                />
              </div>

              {!checkingUser && !effectiveUser && (
                <div
                  style={{
                    marginBottom: 6,
                    fontSize: 13,
                    color: "#f97373",
                  }}
                >
                  You must be logged in to upload.
                </div>
              )}

              {offlineMap && !hasImage && (
                <div
                  style={{
                    marginBottom: 6,
                    fontSize: 13,
                    color: "#f97373",
                  }}
                >
                  This offline map has no image stored and cannot be uploaded.
                </div>
              )}

              {error && (
                <div
                  style={{
                    marginBottom: 8,
                    fontSize: 13,
                    color: "#ff6b6b",
                  }}
                >
                  {error}
                </div>
              )}

              {success && (
                <div
                  style={{
                    marginBottom: 8,
                    fontSize: 13,
                    color: "#4ade80",
                  }}
                >
                  {success}
                </div>
              )}

              <button
                type="submit"
                disabled={saving || !effectiveUser || !hasImage}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "none",
                  background:
                    saving || !effectiveUser || !hasImage
                      ? "#555"
                      : "#22c55e",
                  color: "#000",
                  fontWeight: 600,
                  cursor:
                    saving || !effectiveUser || !hasImage
                      ? "default"
                      : "pointer",
                  marginTop: 6,
                }}
              >
                {saving ? "Uploading…" : "Upload to Point Atlas"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default OfflineUploadToServer;
